import { useMemo } from "react";
import { ProjectStatus } from "@/components/ProjectConfiguration";
import { Status } from "@/types/status";
import { STATUS_TYPE_MAPPING, STATE_KEY_TO_STATUS_ID } from "../constants";
import { buildComprehensiveStatusList } from "./useCanvasStatusManagement";
import { STATUS_LIST } from "../../ProjectList/CanvasKanban";

interface UseSharedStatusManagementProps {
  customProjectStatuses: ProjectStatus[];
}

interface SharedStatusManagementHook {
  comprehensiveStatusList: Status[];
  allStatusOptions: Array<{ id: string; name: string; type: string }>;
  sortedStatuses: Status[];
  projectStatuses: Array<{
    key: string;
    status: string;
    isDefault: boolean;
    type: string;
  }>;
}

export const useSharedStatusManagement = ({
  customProjectStatuses,
}: UseSharedStatusManagementProps): SharedStatusManagementHook => {
  const comprehensiveStatusList = useMemo(() => {
    return buildComprehensiveStatusList(customProjectStatuses);
  }, [customProjectStatuses]);

  // Get all status options for dropdowns and selections
  const allStatusOptions = useMemo(() => {
    const defaultStatuses = STATUS_LIST.map((status) => ({
      id: status.id,
      name: status.name,
      type: status.id,
    }));

    const customStatusOptions = customProjectStatuses.map((status) => ({
      id: status.id,
      name: status.name,
      type: status.type,
    }));

    return [...defaultStatuses, ...customStatusOptions];
  }, [customProjectStatuses]);

  // Get sorted statuses for display (same as comprehensiveStatusList for consistency)
  const sortedStatuses = useMemo(() => {
    return comprehensiveStatusList;
  }, [comprehensiveStatusList]);

  // Get project statuses for kanban columns
  const projectStatuses = useMemo(() => {
    const defaultStatuses = STATUS_LIST.filter((status) =>
      Object.values(STATE_KEY_TO_STATUS_ID).includes(status.id as any),
    ).map((status) => ({
      key: status.id,
      status: status.name,
      isDefault: true,
      type: status.id,
    }));

    const customStatuses = customProjectStatuses.map((status) => ({
      key: status.id,
      status: status.name,
      isDefault: false,
      type: status.type,
    }));

    // Sort custom statuses to appear near their parent types
    const sortedStatuses: Array<{
      key: string;
      status: string;
      isDefault: boolean;
      type: string;
    }> = [];

    defaultStatuses.forEach((defaultStatus) => {
      sortedStatuses.push(defaultStatus);

      // Add custom statuses of the same type immediately after
      customStatuses
        .filter((customStatus) => {
          if (customStatus.type === defaultStatus.type) return true;
          // Handle type variations using our centralized constants
          const mappedType =
            STATUS_TYPE_MAPPING[
              customStatus.type as keyof typeof STATUS_TYPE_MAPPING
            ];
          return mappedType === defaultStatus.type;
        })
        .forEach((customStatus) => {
          sortedStatuses.push(customStatus);
        });
    });

    return sortedStatuses;
  }, [customProjectStatuses]);

  return {
    comprehensiveStatusList,
    allStatusOptions,
    sortedStatuses,
    projectStatuses,
  };
};
